import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { checkForUpdates, skipWaiting } from '../../services/pwa';

interface UpdateNotificationProps {
  className?: string;
  variant?: 'banner' | 'toast';
  autoShow?: boolean;
  onUpdate?: () => void;
  onDismiss?: () => void;
}

// Dismissing snoozes the prompt rather than opting out forever. The previous
// 'yapzap-update-dismissed' flag was written but never cleared, so a single
// dismiss meant the user was never offered another update again.
const SNOOZE_MS = 12 * 60 * 60 * 1000; // 12 hours
const SNOOZE_KEY = 'yapzap-update-snoozed-until';
const LEGACY_DISMISS_KEY = 'yapzap-update-dismissed';

function isSnoozed(): boolean {
  try {
    // Clear the old permanent flag so previously-dismissed users are not stuck.
    if (localStorage.getItem(LEGACY_DISMISS_KEY)) {
      localStorage.removeItem(LEGACY_DISMISS_KEY);
      return false;
    }
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false; // private mode / storage blocked: prefer showing the prompt
  }
}

export const PWAUpdateNotification: React.FC<UpdateNotificationProps> = ({
  className = '',
  variant = 'banner',
  autoShow = true,
  onUpdate,
  onDismiss,
}) => {
  const [show, setShow] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!autoShow) return;

    const handleUpdateAvailable = () => {
      if (!isSnoozed()) {
        setShow(true);
      }
    };

    window.addEventListener('pwa-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateAvailable);
    };
  }, [autoShow]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await skipWaiting();
      try { localStorage.removeItem(SNOOZE_KEY); } catch { /* ignore */ }
      onUpdate?.();
      setShow(false);
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* storage unavailable; prompt will simply reappear next update */
    }
    onDismiss?.();
  };

  if (!show || dismissed) {
    return null;
  }

  const bannerContent = (
    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-lg">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <RefreshCw className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Update Available</h3>
          <p className="text-xs text-green-100">
            A new version of YapZap is ready to install
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={handleUpdate}
          disabled={isUpdating}
          className="px-3 py-1.5 bg-white text-green-600 text-xs font-medium rounded-md hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 text-green-200 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const toastContent = (
    <div className="fixed right-4 z-[60] bottom-[calc(env(safe-area-inset-bottom,0px)+70px)] md:bottom-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <RefreshCw className="w-5 h-5 text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Update Available</p>
          <p className="text-xs text-gray-500 mt-1">
            New version ready to install
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
          >
            {isUpdating ? '...' : 'Update'}
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  switch (variant) {
    case 'toast':
      return toastContent;
    default:
      // The banner had no positioning, so it rendered in normal flow at the end
      // of the page — underneath the fixed bottom nav (Layout.tsx, z-50), only
      // visible by over-scrolling. Sit it above the nav using the same offset
      // convention as mini-music-player, at a z-index above it.
      return (
        <div
          className={`fixed left-3 right-3 z-[60] bottom-[calc(env(safe-area-inset-bottom,0px)+70px)] md:left-auto md:right-4 md:bottom-4 md:max-w-md ${className}`}
        >
          {bannerContent}
        </div>
      );
  }
};

export default PWAUpdateNotification;
